import React, { useState, useEffect } from 'react';
import { Heart, Cake, Briefcase, Baby, Ribbon, Palette, Music, Sparkles, Calendar, Ghost, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { getServices } from '../api';

const iconMap = {
  heart: Heart,
  cake: Cake,
  briefcase: Briefcase,
  baby: Baby,
  ribbon: Ribbon,
  palette: Palette,
  music: Music,
  sparkles: Sparkles,
  calendar: Calendar,
  ghost: Ghost
};

const Services = () => {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (err) {
        setError(t('services.error'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [t, i18n.language]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="services" className="py-24 bg-white">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="services" className="py-24 bg-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 bg-clip-text text-transparent">
              {t('services.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image.startsWith('/api/') 
                      ? `${process.env.REACT_APP_BACKEND_URL}${service.image}` 
                      : service.image}
                    alt={i18n.language === 'en' && service.title_en ? service.title_en : service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <Icon className="w-6 h-6 text-pink-500" />
                    </div>
                  </div>
                </div>

                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                    {i18n.language === 'en' ? (service.title_en || service.title) : service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 text-base leading-relaxed mt-2">
                    {i18n.language === 'en' ? (service.description_en || service.description) : service.description}
                  </CardDescription>
                </CardHeader>

                <CardFooter className="flex items-center justify-between">
                  <span className="text-lg font-bold text-orange-600">
                    {t('services.from')} {service.price}
                  </span>
                  <Button
                    onClick={scrollToContact}
                    variant="ghost"
                    className="text-pink-600 hover:text-pink-700 group/btn font-semibold"
                  >
                    {t('services.reserve')}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-orange-100 via-pink-100 to-fuchsia-100 rounded-2xl p-8 max-w-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Besoin d'une création personnalisée ?
            </h3>
            <p className="text-gray-600 mb-6">
              Chaque événement est unique. Contactez Décors Multifestif pour discuter de votre projet et recevoir un devis sur-mesure.
            </p>
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 hover:from-orange-600 hover:via-pink-700 hover:to-fuchsia-700 text-white px-8 py-6 rounded-full shadow-xl"
            >
              Demander un devis personnalisé
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
